const DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/;

function splitDecimal(value: string): { units: bigint; scale: number } | null {
  const normalized = value.trim().replace(/,/g, "");
  if (!DECIMAL_PATTERN.test(normalized)) return null;
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [whole, fraction = ""] = unsigned.split(".");
  const units = BigInt(`${negative ? "-" : ""}${whole}${fraction}`);
  return { units, scale: fraction.length };
}

export function multiplyDecimalStrings(left: string, right: string): string | null {
  const parsedLeft = splitDecimal(left);
  const parsedRight = splitDecimal(right);
  if (parsedLeft == null || parsedRight == null) return null;
  const scale = parsedLeft.scale + parsedRight.scale;
  const product = parsedLeft.units * parsedRight.units;
  const negative = product < 0n;
  const digits = (negative ? -product : product).toString().padStart(scale + 1, "0");
  if (scale === 0) return `${negative ? "-" : ""}${digits}`;
  const whole = digits.slice(0, -scale);
  const fraction = digits.slice(-scale).replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction === "" ? "" : `.${fraction}`}`;
}

export function decimalStringGreaterThan(left: string, right: string): boolean {
  const parsedLeft = splitDecimal(left);
  const parsedRight = splitDecimal(right);
  if (parsedLeft == null || parsedRight == null) return false;
  const scale = Math.max(parsedLeft.scale, parsedRight.scale);
  const leftUnits = parsedLeft.units * 10n ** BigInt(scale - parsedLeft.scale);
  const rightUnits = parsedRight.units * 10n ** BigInt(scale - parsedRight.scale);
  return leftUnits > rightUnits;
}

export function addDecimalStrings(values: string[]): string | null {
  const parsed = values.map(splitDecimal);
  if (parsed.some((value) => value == null)) return null;
  const decimals = parsed as Array<{ units: bigint; scale: number }>;
  const scale = decimals.reduce((maximum, value) => Math.max(maximum, value.scale), 0);
  const total = decimals.reduce(
    (sum, value) => sum + value.units * 10n ** BigInt(scale - value.scale),
    0n,
  );
  const negative = total < 0n;
  const digits = (negative ? -total : total).toString().padStart(scale + 1, "0");
  if (scale === 0) return `${negative ? "-" : ""}${digits}`;
  const fraction = digits.slice(-scale).replace(/0+$/, "");
  return `${negative ? "-" : ""}${digits.slice(0, -scale)}${fraction === "" ? "" : `.${fraction}`}`;
}

export function formatMoneyString(value: string | null | undefined): string {
  const parsed = splitDecimal(value ?? "");
  if (parsed == null) return "$0.00";
  const negative = parsed.units < 0n;
  const absoluteUnits = parsed.units < 0n ? -parsed.units : parsed.units;
  const scaledCents =
    parsed.scale <= 2
      ? absoluteUnits * 10n ** BigInt(2 - parsed.scale)
      : absoluteUnits / 10n ** BigInt(parsed.scale - 2);
  const digits = scaledCents.toString().padStart(3, "0");
  return `${negative ? "-" : ""}$${digits.slice(0, -2)}.${digits.slice(-2)}`;
}
